import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import UserPool from '../UserPool';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import api from '../api';
import GameYourSatLogo from '../img/GameYourSatLogo.png';

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};


const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(ThemeContext);

  const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: New Password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isEmailValid, setIsEmailValid] = useState(false);
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false);
  
  // Show password states
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateEmail = (emailToValidate) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    setIsEmailValid(validateEmail(val));
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleNewPasswordChange = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    setIsNewPasswordValid(passwordRegex.test(val));
    setDoPasswordsMatch(val === confirmNewPassword && val !== '');
    setErrorMessage('');
  };

  const handleConfirmNewPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmNewPassword(val);
    setDoPasswordsMatch(newPassword === val && val !== '');
    setErrorMessage('');
  };

  const getCognitoUser = useCallback(() => new CognitoUser({ Username: email, Pool: UserPool }), [email]);

  const handleSendCode = () => {
    if (!isEmailValid) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    api.get(`/api/user/get_user_by_email/${email}/`).then((response) => {
      if (response.data === false) {
        setErrorMessage('This account is not registered. Please sign up.');
        setIsLoading(false);
        return;
      }
      else{
        const user = getCognitoUser();
        user.forgotPassword({
        onSuccess: () => {
          setIsLoading(false);
          setSuccessMessage(`Verification code sent to ${email}. Please check your inbox.`);
          setStep(2);
        },
        onFailure: (err) => {
          setIsLoading(false);
          setErrorMessage(err.message || 'Failed to send verification code.');
          console.error('Forgot password error (send code):', err);
      },
    });
      }
    });


  };

  // New handler for verifying the code before showing password fields
  const handleVerifyCode = () => {
    if (!code) {
      setErrorMessage('Please enter the verification code.');
      return;
    }
    // Basic validation: Cognito's confirmPassword will do the actual check, 
    // but we can add a loading state here.
    // If Cognito had a separate verify-only step, we'd use it.
    // For now, we'll assume the code is to be used with new password directly.
    // To make this a distinct step, we just set a success message and move to step 3.
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    // Simulate verification - in a real scenario with a separate verify API, call it here.
    // Since CognitoJS confirmPassword does both, we move to next step optimistically.
    setTimeout(() => { // Simulating a brief check
      setIsLoading(false);
      setSuccessMessage('Code accepted. Please set your new password.');
      setStep(3);
    }, 500);
  };

  const handleChangePassword = () => {
    if (!isNewPasswordValid) {
      setErrorMessage('Password does not meet requirements. (Min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char)');
      return;
    }
    if (!doPasswordsMatch) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    const user = getCognitoUser();

    // The `code` is the verification code from the email
    // The `newPassword` is the new password the user wants to set
    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        setIsLoading(false);
        setSuccessMessage('Password has been reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2500);
      },
      onFailure: (err) => {
        setIsLoading(false);
        setErrorMessage(err.message || 'Failed to reset password. Code might be incorrect or expired.');
        console.error('Confirm password error (change password):', err);
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-2 sm:mb-4"
        >
          <img 
            src={GameYourSatLogo} 
            alt="GameYourSAT Logo" 
            className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto object-contain"
            draggable={false}
          />
        </motion.div>

        <Card delay={0.3} className="p-4 sm:p-6 md:p-8">
          <motion.h1 
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 sm:mb-6 text-center"
            style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f', lineHeight: '1.2', paddingBottom: '0.25rem' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            FORGOT PASSWORD
          </motion.h1>

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center p-3 sm:p-4 rounded-xl mb-3 sm:mb-4 text-sm sm:text-base bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
            >
              {errorMessage}
            </motion.div>
          )}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center p-3 sm:p-4 rounded-xl mb-3 sm:mb-4 text-sm sm:text-base bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
            >
              {successMessage}
            </motion.div>
          )}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={(e) => { e.preventDefault(); handleSendCode(); }} className="space-y-4 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  className={`w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b ${isEmailValid || !email ? 'border-gray-300 dark:border-gray-600' : 'border-red-500'} focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900`}
                  placeholder="Email Address"
                />
                {!isEmailValid && email && (
                  <p className="text-xs sm:text-sm text-red-500 dark:text-red-400 mt-2">Please enter a valid email.</p>
                )}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex justify-end"
              >
                <motion.button
                  type="submit"
                  disabled={!isEmailValid || isLoading}
                  className={`py-2 sm:py-3 px-4 sm:px-8 ${isLoading || !isEmailValid ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'} text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 transition-all`}
                  whileHover={!isLoading && isEmailValid ? { scale: 1.05 } : {}}
                  whileTap={!isLoading && isEmailValid ? { scale: 0.95 } : {}}
                >
                  {isLoading ? 'Sending...' : 'Send Code'}
                </motion.button>
              </motion.div>
            </form>
          )}

          {/* Step 2: Enter Verification Code */}
          {step === 2 && (
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} className="space-y-4 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <input
                  id="email-display"
                  type="email"
                  value={email}
                  disabled
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 focus:outline-none dark:text-gray-100 text-gray-900"
                  placeholder="Email Address"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <input
                  id="code"
                  name="code"
                  type="text"
                  required
                  value={code}
                  onChange={handleCodeChange}
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900"
                  placeholder="Verification Code from Email"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex justify-end"
              >
                <motion.button
                  type="submit"
                  disabled={isLoading || !code || code.length < 6}
                  className={`py-2 sm:py-3 px-4 sm:px-8 ${isLoading || !code || code.length < 6 ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'} text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 transition-all`}
                  whileHover={!isLoading && code && code.length >= 6 ? { scale: 1.05 } : {}}
                  whileTap={!isLoading && code && code.length >= 6 ? { scale: 0.95 } : {}}
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </motion.button>
              </motion.div>
            </form>
          )}

          {/* Step 3: Enter New Password */}
          {step === 3 && (
            <form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }} className="space-y-4 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <input
                  id="email-display-final"
                  type="email"
                  value={email}
                  disabled
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 focus:outline-none dark:text-gray-100 text-gray-900"
                  placeholder="Email Address"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.55 }}
              >
                <input
                  id="code-display-final"
                  type="text"
                  value={code}
                  disabled
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 focus:outline-none dark:text-gray-100 text-gray-900"
                  placeholder="Verified Code"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-2"
              >
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    required
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    className={`w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b ${isNewPasswordValid || !newPassword ? 'border-gray-300 dark:border-gray-600' : 'border-red-500'} focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900`}
                    placeholder="New Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 sm:right-3 top-[50%] -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    {showNewPassword ? (
                      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {newPassword && !isNewPasswordValid && (
                  <p className="text-xs sm:text-sm text-red-500 dark:text-red-400 mt-2">Min 8 chars, 1 upper, 1 lower, 1 digit, 1 special.</p>
                )}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.65 }}
                className="mb-2"
              >
                <div className="relative">
                  <input
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmNewPassword}
                    onChange={handleConfirmNewPasswordChange}
                    className={`w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b ${doPasswordsMatch || !confirmNewPassword ? 'border-gray-300 dark:border-gray-600' : 'border-red-500'} focus:outline-none focus:ring-[#34c759] focus:border-[#34c759] bg-transparent dark:text-gray-100 text-gray-900`}
                    placeholder="Confirm New Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 sm:right-3 top-[50%] -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    {showConfirmPassword ? (
                      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {confirmNewPassword && !doPasswordsMatch && (
                  <p className="text-xs sm:text-sm text-red-500 dark:text-red-400 mt-2">Passwords do not match.</p>
                )}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex justify-end"
              >
                <motion.button
                  type="submit"
                  disabled={isLoading || !newPassword || !confirmNewPassword || !isNewPasswordValid || !doPasswordsMatch}
                  className={`py-2 sm:py-3 px-4 sm:px-8 ${isLoading || !newPassword || !confirmNewPassword || !isNewPasswordValid || !doPasswordsMatch ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'} text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 transition-all`}
                  whileHover={!isLoading && newPassword && confirmNewPassword && isNewPasswordValid && doPasswordsMatch ? { scale: 1.05 } : {}}
                  whileTap={!isLoading && newPassword && confirmNewPassword && isNewPasswordValid && doPasswordsMatch ? { scale: 0.95 } : {}}
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </motion.button>
              </motion.div>
            </form>
          )}
          
          {/* After all form steps */}
          {step >= 1 && (
            <motion.div 
              className="pt-4 sm:pt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <p
                onClick={() => navigate('/login')}
                className="inline-flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xs sm:text-sm cursor-pointer transition-colors"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Back to log in
              </p>
            </motion.div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
